import React, { useState, useEffect } from "react";
import {
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Button,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Typography,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { useAlert } from "../alerts/AlertContext";
import AxiosInstance from "../../services/AxiosInstance";
import { STATUS_URL } from "../../Constants/Constants";

function StatusForm({ open, onClose, editData = null, onSuccess, isEditMode = false }) {
  const [formData, setFormData] = useState({
    name: "",
    seq: "",
    active: true,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const showAlert = useAlert();

  // Initialize form data when dialog opens or editData changes
  useEffect(() => {
    if (open) {
      if (editData) {
        setFormData({
          name: editData.name || "",
          seq: editData.seq || "",
          active: editData.active !== undefined ? editData.active : true,
        });
      } else {
        setFormData({
          name: "",
          seq: "",
          active: true,
        });
      }
      setErrors({});
    }
  }, [open, editData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Status name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Status name must be at least 2 characters";
    } else if (formData.name.trim().length > 50) {
      newErrors.name = "Status name must be less than 50 characters";
    }

    if (!formData.seq.toString().trim()) {
      newErrors.seq = "Sequence is required";
    } else if (isNaN(formData.seq) || parseInt(formData.seq) < 1) {
      newErrors.seq = "Sequence must be a positive number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: formData.name.trim(),
        seq: parseInt(formData.seq),
        active: formData.active,
        ...(isEditMode && { id: editData.id }),
      };

      await AxiosInstance.post(STATUS_URL, payload);

      showAlert(
        `Status ${isEditMode ? "updated" : "created"} successfully!`,
        "success"
      );

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error saving status:", error);
      showAlert(
        `Status ${isEditMode ? "update" : "creation"} failed!`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 450,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isEditMode ? (
            <EditIcon color="primary" />
          ) : (
            <AddIcon color="primary" />
          )}
          <Typography variant="h6" component="span">
            {isEditMode ? "Edit Status" : "Add New Status"}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={loading}
          size="small"
          sx={{ color: "grey.500" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            required
            fullWidth
            id="status-name"
            name="name"
            label="Status Name"
            type="text"
            variant="standard"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            error={Boolean(errors.name)}
            helperText={errors.name}
            disabled={loading}
            sx={{ mb: 3 }}
            InputProps={{
              sx: { borderRadius: 2 },
            }}
          />

          <TextField
            required
            fullWidth
            id="status-seq"
            name="seq"
            label="Sequence"
            type="number"
            variant="standard"
            value={formData.seq}
            onChange={(e) => handleInputChange("seq", e.target.value)}
            error={Boolean(errors.seq)}
            helperText={errors.seq}
            disabled={loading}
            sx={{ mb: 3 }}
            InputProps={{
              sx: { borderRadius: 2 },
            }}
          />

          <FormControl component="fieldset" disabled={loading}>
            <FormLabel
              component="legend"
              sx={{
                color: "text.primary",
                fontWeight: 500,
                mb: 1,
              }}
            >
              Status
            </FormLabel>
            <RadioGroup
              row
              name="active"
              value={formData.active}
              onChange={(e) =>
                handleInputChange("active", e.target.value === "true")
              }
              sx={{ ml: 1 }}
            >
              <FormControlLabel
                value={true}
                control={<Radio />}
                label="Active"
                sx={{ mr: 3 }}
              />
              <FormControlLabel
                value={false}
                control={<Radio />}
                label="Inactive"
              />
            </RadioGroup>
          </FormControl>

          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            {isEditMode
              ? "You are editing an existing status."
              : "You are adding a new status."}
          </Alert>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
          sx={{
            borderRadius: 2,
            minWidth: 120,
          }}
        >
          {loading ? "Saving..." : isEditMode ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default StatusForm;
